'use client';

import { useFormatter } from 'next-intl';
import type { FuturesPair } from '@/lib/api/futures/client';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { TokenIcon } from '@/lib/ui-kit/token-icon';
import { changeDirection, formatPairChange, formatPairPrice } from '@/lib/utils/pairFormat';
import { PairSparkline } from './PairSparkline';
import { SidebarLink } from './SidebarLink';

const PAIR_ICON_SIZE = 24;

/** Figma green/400 and red/400, unlike the search rows' tokens. */
const CHANGE_TEXT = {
  up: 'text-green-400',
  down: 'text-red-400',
  flat: 'text-v2-text-secondary',
} as const;

interface HotFuturesRowProps {
  pair: FuturesPair;
}

/** Icon, ticker, the day's sparkline, then the last price over the day's change. */
export function HotFuturesRow({ pair }: HotFuturesRowProps) {
  const format = useFormatter();

  const price = formatPairPrice(format, pair);
  const change = formatPairChange(format, pair);

  return (
    <SidebarLink
      href={pair.path}
      className="-mx-2 flex items-center gap-2 rounded-[8px] px-2 py-1 outline-none transition-colors duration-150 hover:bg-v2-surface-tertiary focus-visible:ring-2 focus-visible:ring-v2-bg-blue-50"
    >
      <TokenIcon
        symbol={pair.baseCurrency}
        iconUrl={pair.icon}
        size={PAIR_ICON_SIZE}
        fallback="initials"
        className="shrink-0 rounded-full"
      />
      <span className="min-w-0 flex-1 truncate text-[13px] font-[500] leading-4 tracking-[-0.13px] text-v2-text-primary">
        {pair.symbol}
      </span>
      <PairSparkline pairId={pair.id} last={pair.last} changePct={pair.changePct} />
      <span className="flex w-[70px] shrink-0 flex-col items-end gap-1 text-[12px] leading-[1.5] tabular-nums">
        <span className="text-v2-text-primary">{price}</span>
        <span className={CHANGE_TEXT[changeDirection(pair.changePct)]}>{change}</span>
      </span>
    </SidebarLink>
  );
}

/** Placeholder with the row's anatomy and height while pairs and prices load. */
export function HotFuturesRowSkeleton() {
  return (
    <div className="flex items-center gap-2 py-1">
      <Skeleton className="size-6 shrink-0 rounded-full bg-v2-surface-tertiary" />
      <span className="min-w-0 flex-1">
        <Skeleton className="h-3.5 w-20 bg-v2-surface-tertiary" />
      </span>
      <Skeleton className="h-6 w-16 shrink-0 bg-v2-surface-tertiary" />
      <span className="flex h-10 w-[70px] shrink-0 flex-col items-end justify-center gap-2.5">
        <Skeleton className="h-3 w-14 bg-v2-surface-tertiary" />
        <Skeleton className="h-3 w-10 bg-v2-surface-tertiary" />
      </span>
    </div>
  );
}
